// « Claude (Matthieu) » (T-241, présenté en tuile par T-278) — écran Intégrations. C'est l'administrateur qui saisit lui-même la clé.
// T-278 : une TUILE de la grille « Intégrations configurées » (même gabarit que les autres : icône, nom, portée, pastille d'état) ;
// la configuration (clé masquée, modèle, plafond, retrait) s'ouvre au clic dans une fenêtre, comme les autres intégrations.
// ⛔ La clé n'est JAMAIS relue ni préremplie : le serveur ne renvoie que « clé enregistrée oui/non ». Le champ est vidé après l'envoi.
// T-251d : le commutateur de MOTEUR (OmniHub repli | Agentic, manuel, confirmé, journalisé côté serveur) vit dans cette même fenêtre.
// Modèle et plafond quotidien d'échanges réglables ici (coût maîtrisé). Couleurs : tokens var(--oh-*) uniquement (2 modes).
import { useCallback, useEffect, useState } from "react";
import { Bot } from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { OhField, OhInput, OhStatusNote } from "../../components/ui/oh-charte";
import { TintedBadge } from "../../components/ui/oh-tinted";
import Bouton from "../omnihub/communication/boutonBureau";
import TuileIntegration, { libelleEtat } from "./TuileIntegration";
import * as api from "../omnihub/communication/matthieuApi";

const NOM = "Claude (Matthieu)";
const PORTEE = "TEF";

const detail = (e, defaut) => (typeof e?.detail === "string" ? e.detail : defaut);

const MOTEURS = [
  { id: "omnihub", libelle: "OmniHub (repli)", aide: "Matthieu répond tout de suite, avec la clé Claude ci-dessous et le plafond d'échanges par jour." },
  { id: "agentic", libelle: "Agentic", aide: "Les messages sont confiés à l'agent Agentic : la réponse arrive dans la conversation, parfois après quelques secondes. La clé Claude ci-dessous n'est pas utilisée." },
];
const NOM_MOTEUR = Object.fromEntries(MOTEURS.map((m) => [m.id, m.libelle.replace(" (repli)", "")]));
const moteurDe = (c) => (c?.moteur === "agentic" ? "agentic" : "omnihub");
const quand = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};

// `onEtat({ present, connecte })` : la grille s'en sert pour ses compteurs (la tuile en fait partie, connectée ou non).
export default function MatthieuCleCard({ onEtat }) {
  const [ouvert, setOuvert] = useState(false);
  const [etat, setEtat] = useState({ chargement: true, erreur: null, config: null });
  const [cle, setCle] = useState("");
  const [modele, setModele] = useState("");
  const [plafond, setPlafond] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [retrait, setRetrait] = useState(false);
  const [visee, setVisee] = useState(null); // moteur choisi, en attente de confirmation
  const [retour, setRetour] = useState(null); // { ok: bool, texte }

  const appliquer = useCallback((c) => {
    setEtat({ chargement: false, erreur: null, config: c });
    setModele(c.modele || "");
    setPlafond(String(c.plafond_quotidien ?? ""));
  }, []);

  useEffect(() => {
    let actif = true;
    api.lireConfig().then(
      (c) => { if (actif) appliquer(c); },
      (e) => { if (actif) setEtat({ chargement: false, erreur: detail(e, "Réglages de Matthieu indisponibles."), config: null, ferme: e?.status === 401 || e?.status === 403 }); },
    );
    return () => { actif = false; };
  }, [appliquer]);

  const agir = async (appel, succes) => {
    setOccupe(true);
    setRetour(null);
    try {
      appliquer(await appel());
      setRetour({ ok: true, texte: succes });
      return true;
    } catch (e) {
      setRetour({ ok: false, texte: detail(e, "L'enregistrement a échoué.") });
      return false;
    } finally {
      setOccupe(false);
    }
  };

  const changerMoteur = async (vers) => {
    const depuis = moteurDe(etat.config);
    setVisee(null);
    const ok = await agir(() => api.changerMoteur(vers, depuis), `Moteur changé : ${NOM_MOTEUR[vers]}. Le changement est journalisé.`);
    if (!ok) { // 409 : le moteur a changé entre-temps — on relit pour que l'écran dise la vérité
      try { appliquer(await api.lireConfig()); } catch { /* le message d'erreur reste affiché */ }
    }
  };

  const c = etat.config;
  const moteurActuel = moteurDe(c);
  const present = !etat.chargement && !etat.ferme;
  const connecte = !!c?.cle_enregistree;
  useEffect(() => { if (onEtat && !etat.chargement) onEtat({ present, connecte }); }, [onEtat, etat.chargement, present, connecte]);
  if (etat.chargement || etat.ferme) return null; // module non activé ou rôle sans droit : la tuile n'a pas lieu d'être
  const etatTuile = !c ? "indisponible" : connecte ? "connecte" : "non-connecte";
  const plafondNombre = Number(plafond);
  const plafondValide = Number.isInteger(plafondNombre) && plafondNombre >= 1 && plafondNombre <= 2000;

  return (
    <>
      <TuileIntegration Icone={Bot} couleur="violet" nom={NOM} portee={PORTEE} etat={etatTuile} onOuvrir={() => setOuvert(true)}
        data-testid="matthieu-tuile" aria-label={`${NOM}, ${PORTEE}, ${libelleEtat(etatTuile)}`} />

      <Dialog open={ouvert} onOpenChange={setOuvert}>
        <DialogContent data-testid="matthieu-cle-carte" className="max-w-lg max-h-[90vh] overflow-y-auto pr-6"
          style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}>
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
              {NOM}
              {c && <TintedBadge color={c.cle_enregistree ? "green" : "amber"} data-testid="matthieu-cle-etat">{c.cle_enregistree ? "Connecté" : "Non connecté"}</TintedBadge>}
            </DialogTitle>
            <DialogDescription style={{ color: "var(--oh-text-secondary)" }}>Permet de parler à Matthieu depuis le Bureau. Choisit le moteur et garde la clé Claude de repli : chiffrée, jamais affichée.</DialogDescription>
          </DialogHeader>
          {etat.erreur && <OhStatusNote variant="info" data-testid="matthieu-cle-indisponible">{etat.erreur}</OhStatusNote>}
          {c && (
            <div className="space-y-3">
              <section aria-label="Moteur de Matthieu" data-testid="matthieu-moteur" className="space-y-2">
                <p id="matthieu-moteur-titre" style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "var(--oh-text)" }}>Moteur</p>
                <div role="radiogroup" aria-labelledby="matthieu-moteur-titre" className="flex flex-wrap gap-2">
                  {MOTEURS.map((m) => {
                    const actif = moteurActuel === m.id;
                    return (
                      <button key={m.id} type="button" role="radio" aria-checked={actif} data-testid={`matthieu-moteur-${m.id}`} disabled={occupe || actif}
                        onClick={() => setVisee(m.id)}
                        style={{ minHeight: 36, padding: "0 var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", fontSize: 14, fontWeight: actif ? 500 : 400,
                          border: actif ? "0.5px solid var(--oh-accent)" : "0.5px solid var(--oh-border)",
                          background: actif ? "var(--oh-accent-bg)" : "transparent", color: actif ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
                          cursor: actif || occupe ? "default" : "pointer" }}>
                        {m.libelle}
                      </button>
                    );
                  })}
                </div>
                <p style={{ margin: 0, fontSize: 13, color: "var(--oh-text-secondary)" }} data-testid="matthieu-moteur-aide">{MOTEURS.find((m) => m.id === moteurActuel).aide}</p>
                {visee && (
                  <OhStatusNote variant="info" data-testid="matthieu-moteur-confirmation" title={`Passer de ${NOM_MOTEUR[moteurActuel]} à ${NOM_MOTEUR[visee]} ?`}>
                    Les prochains messages à Matthieu seront traités par {NOM_MOTEUR[visee]}. Vous pouvez revenir en arrière à tout moment ; le changement est journalisé avec votre nom.
                  </OhStatusNote>
                )}
                {visee && (
                  <div className="flex flex-wrap gap-2">
                    <Bouton type="secondaire" data-testid="matthieu-moteur-confirmer" disabled={occupe} onClick={() => changerMoteur(visee)}>Confirmer le changement</Bouton>
                    <Bouton data-testid="matthieu-moteur-annuler" onClick={() => setVisee(null)}>Annuler</Bouton>
                  </div>
                )}
                <div data-testid="matthieu-moteur-journal">
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: "var(--oh-text-secondary)" }}>Derniers changements</p>
                  {(c.journal_moteur || []).length === 0
                    ? <p style={{ margin: 0, fontSize: 13, color: "var(--oh-text-muted)" }}>Aucun changement enregistré.</p>
                    : (
                      <ul style={{ margin: 0, paddingLeft: "var(--oh-space-4)", fontSize: 13, color: "var(--oh-text)" }}>
                        {c.journal_moteur.map((j, i) => (
                          <li key={`${j.quand}-${i}`}>{quand(j.quand)} — {j.par_nom || "Super administrateur"} : {NOM_MOTEUR[j.de] || j.de} → {NOM_MOTEUR[j.vers] || j.vers}</li>
                        ))}
                      </ul>
                    )}
                </div>
              </section>

              <OhField label="Clé Claude" htmlFor="matthieu-cle" hint={c.cle_enregistree ? "Une clé est enregistrée. Saisissez-en une nouvelle pour la remplacer." : "Collez ici la clé que vous avez créée chez le fournisseur."}>
                <OhInput id="matthieu-cle" data-testid="matthieu-cle-champ" type="password" name="matthieu-cle" autoComplete="new-password" spellCheck={false}
                  value={cle} maxLength={300} placeholder={c.cle_enregistree ? "Clé enregistrée (non affichée)" : "Clé Claude"} onChange={(e) => setCle(e.target.value)} />
              </OhField>
              <div className="flex flex-wrap gap-2">
                <Bouton type="secondaire" data-testid="matthieu-cle-enregistrer" disabled={occupe || cle.trim().length === 0}
                  onClick={async () => { const v = cle.trim(); setCle(""); await agir(() => api.enregistrerConfig({ cle: v }), "Clé enregistrée. Matthieu est connecté."); }}>
                  Enregistrer la clé
                </Bouton>
                {c.cle_enregistree && !retrait && <Bouton data-testid="matthieu-cle-retirer" disabled={occupe} onClick={() => setRetrait(true)}>Retirer la clé</Bouton>}
                {c.cle_enregistree && retrait && (
                  <>
                    <Bouton data-testid="matthieu-cle-retirer-confirmer" disabled={occupe}
                      onClick={async () => { setRetrait(false); await agir(() => api.retirerCle(), "Clé retirée. Matthieu n'est plus connecté."); }}>Confirmer le retrait</Bouton>
                    <Bouton data-testid="matthieu-cle-retirer-annuler" onClick={() => setRetrait(false)}>Annuler</Bouton>
                  </>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <OhField label="Modèle" htmlFor="matthieu-modele" hint="Nom du modèle Claude utilisé pour les réponses.">
                  <OhInput id="matthieu-modele" data-testid="matthieu-modele" value={modele} onChange={(e) => setModele(e.target.value)} />
                </OhField>
                <OhField label="Plafond d'échanges par jour" htmlFor="matthieu-plafond" hint={`Utilisés aujourd'hui : ${c.utilises_aujourdhui ?? 0}. Entre 1 et 2000.`}
                  error={plafond !== "" && !plafondValide ? "Entier entre 1 et 2000." : undefined}>
                  <OhInput id="matthieu-plafond" data-testid="matthieu-plafond" type="number" inputMode="numeric" min={1} max={2000} value={plafond} onChange={(e) => setPlafond(e.target.value)} />
                </OhField>
              </div>
              <Bouton data-testid="matthieu-reglages-enregistrer" disabled={occupe || !plafondValide || modele.trim().length === 0}
                onClick={() => agir(() => api.enregistrerConfig({ modele: modele.trim(), plafond_quotidien: plafondNombre }), "Réglages enregistrés.")}>
                Enregistrer les réglages
              </Bouton>
            </div>
          )}
          {retour && <OhStatusNote variant={retour.ok ? "success" : "error"} data-testid="matthieu-cle-retour" style={{ marginTop: "var(--oh-space-3)" }}>{retour.texte}</OhStatusNote>}
        </DialogContent>
      </Dialog>
    </>
  );
}
